<template>
  <div>
    <h2 class="mb-2">請求詳細</h2>
    <div class="d-flex mb-4" style="align-items: center">
      <h3 class="mb-2">{{ schoolName }} {{ year }}年 {{ month }}月</h3>
      <v-spacer />
      <div style="width: 180px">
        <v-select
          variant="outlined"
          density="compact"
          hide-details
          :items="invoiceStatusOptions"
          item-value="value"
          item-title="label"
          :disabled="isFetching"
          v-model="invoiceStatus"
          :readonly="invoiceStatus === 'invoiceSettled'"
        />
      </div>
      <v-btn
        v-if="currentInvoiceStatus !== invoiceStatus"
        color="primary"
        depressed
        class="mx-1"
        @click="updateStatus"
      >
        保存
      </v-btn>
    </div>

    <!-- <v-progress-linear v-if="isFetching" class="my-4" indeterminate /> -->

    <v-data-table
      :headers="headers"
      :items="tableData"
      v-model:expanded="expanded"
      item-value="childId"
      single-expand
      :loading="isFetching"
    >
      <!-- 請求書ボタン (カスタムセル) -->
      <template v-slot:[`item.childId`]="{ item }">
        <div v-if="invoiceStatus === 'invoiceSettled'">
          <!-- Vuetify 3: depressedは廃止、variant="flat"を使用 -->
          <v-btn variant="flat" color="primary" @click="openInvoice(item)">
            <!-- Vuetify 3: leftは廃止、startを使用 -->
            <v-icon start>mdi-tray-arrow-down</v-icon>
            請求書
          </v-btn>
        </div>
      </template>

      <!-- 集金方法 (カスタムセル) -->
      <template v-slot:[`item.collectionMethod`]="{ item }">
        {{ getPaymentMethodText(item.collectionMethod) }}
      </template>

      <!-- 請求詳細の展開行 (expanded-item から expanded-row に変更) -->
      <template v-slot:expanded-row="{ columns, item }">
        <!-- Vuetify 3: headers ではなく columns.length を使用 -->
        <td :colspan="columns.length" class="px-0">
          <div class="expand-cell">
            <div class="invoice-detail-table">
              <!-- 月極保育料 -->
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">月極保育料</div>
                <div class="invoice-detail-table__value">
                  {{ item.monthlyNurseryFee }}
                </div>
              </div>
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">月極保育料 差額</div>
                <div class="invoice-detail-table__value">
                  <v-text-field
                    hide-details
                    density="compact"
                    variant="outlined"
                    type="number"
                    style="font-size: 14px !important"
                    :readonly="invoiceStatus === 'invoiceSettled'"
                    v-model="item.monthlyNurseryFeeDiff"
                  />
                </div>
              </div>

              <v-divider />

              <!-- 月極延長料 -->
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">月極延長料</div>
                <div class="invoice-detail-table__value">
                  {{ item.monthlyAdditionalFee }}
                </div>
              </div>
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">月極延長料 差額</div>
                <div class="invoice-detail-table__value">
                  <v-text-field
                    hide-details
                    density="compact"
                    variant="outlined"
                    type="number"
                    style="font-size: 14px !important"
                    :readonly="invoiceStatus === 'invoiceSettled'"
                    v-model="item.monthlyAdditionalFeeDiff"
                  />
                </div>
              </div>

              <v-divider />

              <!-- 一時保育料 -->
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">一時保育料</div>
                <div class="invoice-detail-table__value">
                  {{ item.tempNurseryFee }}
                </div>
              </div>
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">一時保育料 差額</div>
                <div class="invoice-detail-table__value">
                  <v-text-field
                    hide-details
                    density="compact"
                    variant="outlined"
                    type="number"
                    style="font-size: 14px !important"
                    :readonly="invoiceStatus === 'invoiceSettled'"
                    v-model="item.tempNurseryFeeDiff"
                  />
                </div>
              </div>

              <v-divider />

              <!-- スポット延長料 -->
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">スポット延長料</div>
                <div class="invoice-detail-table__value">
                  {{ item.spotAdditionalFee }}
                </div>
              </div>
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">スポット延長料 差額</div>
                <div class="invoice-detail-table__value">
                  <v-text-field
                    hide-details
                    density="compact"
                    variant="outlined"
                    type="number"
                    style="font-size: 14px !important"
                    :readonly="invoiceStatus === 'invoiceSettled'"
                    v-model="item.spotAdditionalFeeDiff"
                  />
                </div>
              </div>

              <v-divider />

              <!-- 主食・副食費 -->
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">主食・副食費</div>
                <div class="invoice-detail-table__value">
                  {{ item.foodServiceFee }}
                </div>
              </div>
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">主食・副食費 差額</div>
                <div class="invoice-detail-table__value">
                  <v-text-field
                    hide-details
                    density="compact"
                    variant="outlined"
                    type="number"
                    style="font-size: 14px !important"
                    :readonly="invoiceStatus === 'invoiceSettled'"
                    v-model="item.foodServiceFeeDiff"
                  />
                </div>
              </div>

              <v-divider />

              <!-- その他（ループ処理） -->
              <div class="invoice-detail-table__row">
                <div class="invoice-detail-table__key">その他</div>
                <div class="invoice-detail-table__value">
                  <div v-for="(extraService, i) in item.extraServices" :key="i">
                    <div class="invoice-detail-table__row">
                      <div class="invoice-detail-table__key nest">
                        {{ extraService.serviceName }}
                      </div>
                      <div class="invoice-detail-table__value nest">
                        {{ extraService.fee }}
                      </div>
                    </div>
                    <div class="invoice-detail-table__row">
                      <div class="invoice-detail-table__key nest">
                        {{ extraService.serviceName }} 差額
                      </div>
                      <div class="invoice-detail-table__value nest">
                        <v-text-field
                          hide-details
                          density="compact"
                          variant="outlined"
                          type="number"
                          style="font-size: 14px !important"
                          :readonly="invoiceStatus === 'invoiceSettled'"
                          v-model="extraService.adjustmentAmount"
                        />
                      </div>
                    </div>
                    <v-divider v-if="i !== item.extraServices.length - 1" />
                  </div>
                </div>
              </div>
            </div>

            <div class="py-4 d-flex justify-center">
              <v-btn
                variant="flat"
                size="large"
                color="primary"
                @click="saveRecord(item)"
                :disabled="currentInvoiceStatus !== 'applied'"
                :loading="saveLoading"
              >
                保存
              </v-btn>
            </div>
          </div>
        </td>
      </template>
    </v-data-table>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { schoolInvoiceModule } from "@/store/viewModule/schoolInvoiceModule";
import dayjs from "dayjs";
import { OperationChildInvoiceResponse } from "chaild-api/lib";
import { ExtraServiceDetailType } from "chaild-api/src/model/mysql/entity/invoiceEntity";
import ApiInvoice from "@/api/ApiInvoice";
import { PaymentMethodCategoryType } from "chaild-api/src/constant/paymentMethodCategory";
import CommonUtil from "@/utils/CommonUtils";
import { invoiceStatusItems, paymentMethodItems } from "@/constants/invoice";

interface DataType {
  saveLoading: boolean;
  nurseryId: number | null;
  yearMonth: string | null;
  isFetching: boolean;
  expanded: Array<any>;
  headers: Array<any>;
  tableData: TableData[];
  invoiceStatusOptions: InvoiceStatusOption[];
  invoiceStatus: string | null;
  paymentMethodItems: any[];
}

interface InvoiceStatusOption {
  value: string;
  label: string;
}

interface TableData {
  childId: number;
  childName: string;
  className: string;
  collectionMethod: PaymentMethodCategoryType | null;
  monthlyNurseryFee: number;
  monthlyNurseryFeeDiff: number;
  tempNurseryFee: number;
  tempNurseryFeeDiff: number;
  monthlyAdditionalFee: number;
  monthlyAdditionalFeeDiff: number;
  spotAdditionalFee: number;
  spotAdditionalFeeDiff: number;
  foodServiceFee: number;
  foodServiceFeeDiff: number;
  extraServiceFee: number;
  extraServiceFeeDiff: number;
  accountsRecievable: number;
  totalSales: number;
  totalAmount: number;
  extraServices: ExtraServiceDetailType[];
}

export default defineComponent({
  data: (): DataType => ({
    saveLoading: false,
    nurseryId: null,
    yearMonth: dayjs().format("YYYY-MM"),
    isFetching: false,
    expanded: [],
    headers: [
      { title: "園児氏名", value: "childName" },
      { title: "クラス名", value: "className" },
      { title: "集金方法", value: "collectionMethod" },
      { title: "売掛金", value: "accountsRecievable" },
      { title: "売上総額", value: "totalSales" },
      { title: "請求総額", value: "totalAmount" },
      { title: "", value: "childId" },
      { title: "", value: "data-table-expand" },
    ],
    invoiceStatusOptions: invoiceStatusItems,
    // invoiceStatusOptions: [
    //   { value: 'applied', label: '施設入力完了' },
    //   { value: 'salesSettled', label: '売上確定' },
    //   { value: 'invoiceSettled', label: '請求確定' },
    // ],
    invoiceStatus: null,
    tableData: [],
    paymentMethodItems: paymentMethodItems,
  }),
  computed: {
    schoolInvoice() {
      return schoolInvoiceModule.invoiceDetail;
    },
    currentInvoiceStatus(): string | null {
      if (this.schoolInvoice) {
        return this.schoolInvoice.status;
      }
      return null;
    },
    invoices(): OperationChildInvoiceResponse[] {
      if (this.schoolInvoice) {
        return this.schoolInvoice.invoices;
      }
      return [];
    },
    schoolName(): string {
      if (this.schoolInvoice) {
        return this.schoolInvoice.nurseryName;
      }
      return "";
    },
    year(): number {
      const year = parseInt(dayjs(this.yearMonth).format("YYYY"));
      return year;
    },
    month(): number {
      return parseInt(dayjs(this.yearMonth).format("MM"));
    },
    importStatus(): string | null {
      if (schoolInvoiceModule.importStatus) {
        if (schoolInvoiceModule.importStatus === "inProgress") {
          return "進行中";
        }
        if (schoolInvoiceModule.importStatus === "failed") {
          return "取り込み失敗";
        }
        if (schoolInvoiceModule.importStatus === "success") {
          return "取り込み完了";
        }
      }
      if (schoolInvoiceModule.uploadId) {
        return "進行中";
      }
      return null;
    },
    exportStatus(): string | null {
      if (schoolInvoiceModule.exportStatus) {
        if (schoolInvoiceModule.exportStatus === "inProgress") {
          return "進行中";
        }
        if (schoolInvoiceModule.exportStatus === "failed") {
          return "書き出し失敗";
        }
        if (schoolInvoiceModule.exportStatus === "success") {
          return "書き出し完了";
        }
      }
      if (schoolInvoiceModule.uploadId) {
        return "進行中";
      }
      return null;
    },
  },
  methods: {
    async getSchoolInvoice() {
      if (this.nurseryId && this.yearMonth) {
        const year = CommonUtil.getFiscalYear(this.yearMonth);
        const month = dayjs(this.yearMonth).month() + 1;

        this.isFetching = true;
        await Promise.all([
          schoolInvoiceModule.getSchoolInvoice({
            nurseryId: this.nurseryId,
            year,
            month,
          }),
        ]).catch();
        this.isFetching = false;
      }
    },
    async saveRecord(item: TableData) {
      if (this.yearMonth) {
        this.saveLoading = true;
        const year = CommonUtil.getFiscalYear(this.yearMonth);
        const month = dayjs(this.yearMonth).month() + 1;
        this.isFetching = true;
        await schoolInvoiceModule.updateChildRecrod({
          childId: item.childId,
          year: String(year),
          month: String(month),
          monthlyAdditionalFeeDiff: parseInt(`${item.monthlyAdditionalFeeDiff}`, 10),
          tempNurseryFeeDiff: parseInt(`${item.tempNurseryFeeDiff}`, 10),
          monthlyNurseryFeeDiff: parseInt(`${item.monthlyNurseryFeeDiff}`, 10),
          spotAdditionalFeeDiff: parseInt(`${item.spotAdditionalFeeDiff}`, 10),
          foodServiceFeeDiff: parseInt(`${item.foodServiceFeeDiff}`, 10),
          extraServiceDiffs: item.extraServices.map((es) => ({
            extraServiceBillingDetailId: es.extraServiceBillingDetailId,
            adjustmentAmount: parseInt(`${es.adjustmentAmount}`, 10),
          })),
        });
        this.isFetching = false;
        this.saveLoading = false;
      }
    },
    async updateStatus() {
      if (this.invoiceStatus) {
        this.isFetching = true;
        if (this.nurseryId) {
          let nurseryIds: number[] = [];
          nurseryIds.push(Number(this.nurseryId));
          const response = await schoolInvoiceModule
            .updateStatus({
              nurseryIds: nurseryIds,
              year: this.year,
              month: this.month,
              status: this.invoiceStatus,
            })
            .catch(() => (this.isFetching = false));
          if (response) {
            alert("確定しました");
            await this.getSchoolInvoice();
          }
        }
        this.isFetching = false;
      }
    },
    calcTableData() {
      this.tableData = this.invoices.map((i) => {
        const childName = `${i.child.lastName} ${i.child.firstName}`;
        if (!i.invoice) {
          return {
            childId: i.child.childId,
            childName,
            className: "",
            collectionMethod: null,
            monthlyNurseryFee: 0,
            monthlyNurseryFeeDiff: 0,
            tempNurseryFee: 0,
            tempNurseryFeeDiff: 0,
            monthlyAdditionalFee: 0,
            monthlyAdditionalFeeDiff: 0,
            spotAdditionalFee: 0,
            spotAdditionalFeeDiff: 0,
            foodServiceFee: 0,
            foodServiceFeeDiff: 0,
            extraServiceFee: 0,
            extraServiceFeeDiff: 0,
            accountsRecievable: 0,
            totalSales: 0,
            totalAmount: 0,
            extraServices: [],
          };
        }
        const monthlyNursery = i.invoice.monthlyNurseryFee + i.invoice.monthlyNurseryFeeDiff;
        const tempNursery = i.invoice.tempNurseryFee + i.invoice.tempNurseryFeeDiff;
        const monthlyAdditional =
          i.invoice.monthlyAdditionalFee + i.invoice.monthlyAdditionalFeeDiff;
        const spotAdditional = i.invoice.spotAdditionalFee + i.invoice.spotAdditionalFeeDiff;
        const foodService = i.invoice.foodServiceFee + i.invoice.foodServiceFeeDiff;
        const extraService = i.invoice.extraServiceFee + i.invoice.extraServiceFeeDiff;

        const totalSales =
          monthlyNursery +
          tempNursery +
          monthlyAdditional +
          spotAdditional +
          foodService +
          extraService;
        const totalAmount = totalSales + i.invoice.accountsReceivable;

        // クラス名探し
        const findClass = i.child.nurseryClasses.find((nc) => nc.year === this.year);
        const className = findClass ? findClass.className : "";

        return {
          childId: i.child.childId,
          childName,
          className: className,
          collectionMethod: i.invoice.paymentMethod || null,
          monthlyNurseryFee: i.invoice.monthlyNurseryFee,
          monthlyNurseryFeeDiff: i.invoice.monthlyNurseryFeeDiff,
          tempNurseryFee: i.invoice.tempNurseryFee,
          tempNurseryFeeDiff: i.invoice.tempNurseryFeeDiff,
          monthlyAdditionalFee: i.invoice.monthlyAdditionalFee,
          monthlyAdditionalFeeDiff: i.invoice.monthlyAdditionalFeeDiff,
          spotAdditionalFee: i.invoice.spotAdditionalFee,
          spotAdditionalFeeDiff: i.invoice.spotAdditionalFeeDiff,
          foodServiceFee: i.invoice.foodServiceFee,
          foodServiceFeeDiff: i.invoice.foodServiceFeeDiff,
          extraServiceFee: i.invoice.extraServiceFee,
          extraServiceFeeDiff: i.invoice.extraServiceFeeDiff,
          accountsRecievable: i.invoice.accountsReceivable,
          totalSales: totalSales,
          totalAmount: totalAmount,
          extraServices: i.invoice.extraServiceDetails,
        };
      });

      if (this.schoolInvoice) {
        this.invoiceStatus = this.schoolInvoice.status;
      }
    },
    async openInvoice(item: TableData) {
      // itemの中にchildIdが正しく存在するかチェック
      if (!item?.childId) {
        console.error("childIdが見つかりません:", item);
        return;
      }

      this.isFetching = true;
      try {
        const response = await ApiInvoice.donwloadInvoice({
          childId: item.childId,
          year: this.year,
          month: this.month,
        });

        if (response && response.url) {
          window.open(response.url, '_blank'); // _blankを明示
        } else {
          alert("請求書のURLが取得できませんでした。");
        }
      } catch (error: any) {
        // [object Object] の中身を確認するためにログ出力
        console.error("APIエラー詳細:", error);
      } finally {
        // 成功・失敗に関わらず必ず実行
        this.isFetching = false;
      }
    },
    // getPaymentMethodText(value: string): string {
    //   if (value == 'card') { return 'クレジットカード'; }
    //   else if (value == 'withdrawal') { return '口座振替'; }
    //   else if (value == 'invoice') { return '振り込み'; }
    //   else { return '集金'; }
    // },
    getPaymentMethodText(value?: string): string {
      if (!value) {
        return "集金";
      }
      return this.paymentMethodItems.find((p) => p.value == value)?.text || "";
    },
  },
  watch: {
    invoices: {
      handler() {
        this.calcTableData();
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    const params = this.$route.params;
    if (params.nurseryId && params.yearMonth) {
      try {
        const nurseryId = parseInt(params.nurseryId as string);
        const yearMonth = params.yearMonth as string;
        this.nurseryId = nurseryId;
        this.yearMonth = yearMonth;
        this.getSchoolInvoice();
      } catch {
        return;
      }
    }

    if (this.yearMonth) {
      console.log(CommonUtil.getFiscalYear(this.yearMonth));
    }
  },
});
</script>
<style lang="scss" scoped>
::v-deep {
  .v-data-table__expanded__content {
    box-shadow: none !important;
  }
}

.expand-cell {
  padding: 8px;
  background-color: #fafafa;
}

.invoice-detail-table {
  padding: 16px;
  background-color: white;
}

.invoice-detail-table__row {
  margin: 4px 0;
  padding: 8px 4px;

  min-height: 48px;

  display: flex;
  align-items: center;

  &:hover {
    background-color: #f0f0f0;
  }
}

.invoice-detail-table__key {
  flex: 1;
  font-weight: bold;

  &.nest {
    flex: 1;
  }
}

.invoice-detail-table__value {
  flex: 3;

  &.nest {
    flex: 1;
  }
}
::v-deep(.v-data-table-footer .v-select) {
  min-width: 120px;
}
</style>
